import axios, {AxiosError} from "axios";
import type {AxiosRequestConfig, ResponseType} from "axios";
import {ref, type Ref} from "vue";
import {randomUuid, sha256} from "@/practice/crypto";
import {Message, type Agent, type LanguageSettings, type UserSettings, CardType, State, Rating, type Word, type Card, type Review, type StudyStats, type HistoricalStats, type Session, SessionType, type HistorySessionSummary } from "@/types/model";
import type {AgentTemplate, Voice} from "@/types/agents";
import {LOG_SENSITIVE_DATA} from "@/practice/util";
import {clearSavedWordsCache} from "@/practice/savedWordsCache";
import {clearHistoryCache} from "@/practice/historyCache";
import {ConditionalResponseCache, conditionalResponseCache, type ConditionalResponse} from "@/practice/conditionalResponseCache";
import {
    clearAuthenticatedSession,
    consumeAuthenticationFailure,
    markAuthenticatedSession,
    notifyAuthenticationFailure,
} from "@/practice/authSession";
import type {ClientMediaTelemetryPayload} from "@/practice/media/types";
import {getMediaSessionCredentials} from "@/practice/media/credentials";
import {
    ClientMediaTelemetryTracker,
    EMPTY_NETWORK_SNAPSHOT,
} from "@/practice/media/networkTelemetry";
import {
    SessionStartOwnership,
    type SessionStartAttempt,
} from "@/practice/media/SessionStartOwnership";

axios.defaults.withCredentials = true;

const START_SESSION_PREFLIGHT_TIMEOUT_MS = 1000;
const EXPECTED_UNAUTHENTICATED_ENDPOINTS = new Set([
    '/login',
    '/create_user',
    '/check_username',
    '/reset_password',
    '/login_with_code',
]);

function isExpectedUnauthenticatedUrl(url: string): boolean {
    const path = url.split('?')[0] ?? url;
    if (EXPECTED_UNAUTHENTICATED_ENDPOINTS.has(path)) {
        return true;
    }
    return path.startsWith('/i/');
}

export type ApiRes<T> = {
    data: Ref<T | null>,
    isFetching: Ref<boolean>,
    error: Ref<number | null>
}
export function apiRes<T>() : ApiRes<T> {
    const data: Ref<T|null> = ref(null);
     return {
         data,
         isFetching: ref(false),
         error: ref(null)
     }
}


export type CheckUsernameResponse = {
    usernameExists: boolean
}

export type CreateUserResponse = {
    message: string
}

export type UpdateUserResponse = {}
export type UpdateAgentCustomInstructionsResponse = {}
export type HideAgentTemplateResponse = { success: boolean }

export type LoginResponse = {
    success: boolean,
    message: string,
    user: UserSettings | null
}

export type LogoutResponse = {}

export type GetLoginStatusResponse = {
    loggedIn: boolean
}

export type GetUserResponse = {
    user: UserSettings
}

export type GetReferralOfferResponse = {
    code: string
    url: string
    shareCount: number
    trialCount: number
    pendingCount: number
    paidCount: number
    giftCardsIssuedUsd: number
}

export type RecordInviteClickResponse = {
    valid: boolean
    code: string | null
    discountApplied: boolean
}

export type CheckSentenceResponse = {
    feedback: string,
    score: number
}

export type GetLeaderboardResponse = {
    leaderboard: {name: string, userId: string, language: string, totalDuration: number, streak?: number, ranking: number}[],
    streakLeaderboard?: {name: string, userId: string, language: string, streak: number, ranking: number}[],
    currentUser?: {name: string, userId: string, language: string, totalDuration: number, streak?: number, ranking: number},
    currentUserStreakRanking?: {name: string, userId: string, language: string, streak: number, ranking: number}
}

export type GetUserUsageResponse = {
    weeklyDuration: number,
    totalDuration: number,
    remainingMinutes?: number,
    remainingSeconds?: number
}

export type GetSubscriptionDetailsResponse = {
    subscription: {
        subscriptionId: string,
        platform: 'apple' | 'google' | 'stripe' | 'stripe_lifetime' | 'manual',
        creationDate: number,
        expirationDate: number,
        status: string,
        autoRenewEnabled?: boolean | null,
        stripeSubscriptionId?: string,
        stripeCheckoutSessionId?: string,
        stripePaymentIntentId?: string | null,
        stripeCustomerId?: string,
        appleOriginalTxId?: string,
        googleLastOrderId?: string,
    }
}

export type GetVoicesResponse = {
    voices: Voice[]
}

export type GetAvailableAgentTemplatesResponse = {
    agentTemplates: AgentTemplate[]
}

export type GetWebRTCCredentialsResponse = {
    username: string,
    password: string
}   
export type MediaProvider = 'daily' | 'livekit'

export type StartSessionMedia = {
    provider: MediaProvider,
    endpointId: string | null,
    url: string,
    roomName: string,
    token: string,
    tokenExpiresAt: number | null
}

export type StartSessionResponse = {
    room: {
        id: string,
        name: string,
        url: string
    },
    token: string,
    sessionId: string,
    sessionOwnershipToken: string,
    media?: StartSessionMedia
}

export type StartSessionPreflightResponse = {
    eligible: boolean,
    mediaProviderHint?: MediaProvider
}

export type StartSessionRequest = {
    deviceType: 'web',
    sessionType: SessionType,
    sessionRequestId: string,
    requiredMediaProvider?: 'livekit',
    supersedesSessionId?: string,
    sessionOwnershipToken?: string,
}

export type SubscribeResponse = {
    url: string
}

export type SubscribeTrackingPayload = {
    redirectClickId: string | null,
    checkoutEventId: string | null,
    subscriptionEventId: string | null,
    trackingValue: number | null,
    trackingCurrency: string | null,
}

export type TrackPaidSocialEventPayload = {
    eventType: string,
    eventId: string,
    pageUrl: string | null,
    value: number | null,
    currency: string | null,
}

export type PriceTier = {
    amount: number,
    currency: string,
    priceId: string,
    discountedAmount?: number,
}

// One entry of the paywall-pricing-test plan list served by /get_prices: the user's
// arm decides which plans appear, their order, and whether each is a prominent card
// or hidden behind "See all plans". periodMonths null = lifetime (one-time purchase).
export type PaywallPlanPrice = {
    key: 'monthly' | 'sixMonth' | 'yearly' | 'lifetime',
    periodMonths: number | null,
    prominent: boolean,
    priceId: string,
    amount: number,
    currency: string,
    discountedAmount?: number,
}

export type GetPricesResponse = {
    priceData: {
        monthly: PriceTier,
        sixMonth?: PriceTier,
        yearly: PriceTier,
        preferredCurrency: string,
        // Present on servers with the paywall pricing test deployed; older responses
        // only carry the legacy arm-A tiers above.
        arm?: string,
        assignedArm?: string,
        servedFallback?: boolean,
        plans?: PaywallPlanPrice[],
        inviteDiscountPercent?: number | null,
    }
}

export type GetPaywallCopyConfigResponse = {
  arm: "A" | "B";
}

export type GetPriceResponse = {
    price: PriceTier
}

export type CreateStripePortalSessionResponse = {
    url?: string
    alreadyCancelled?: boolean
    expirationDate?: number
}

export type LifetimeUpgradeResponse = {
    unavailable?: boolean;
    upgradeId?: string;
    platform?: "stripe" | "apple" | "google";
    state: "none" | "prepared" | "checkout" | "abandoned" | "paid" | "complete" | "expired" | "review";
    fullAmount?: number;
    amountDue?: number;
    upgradeDiscount?: number;
    currency?: string;
    renewalDate?: number;
    renewing?: boolean;
    url?: string;
};

export type PlanChangeResponse = {
    lifetimePending?: boolean;
    unavailable?: boolean;
    platform?: "stripe" | "apple" | "google";
    currentPeriodMonths?: number;
    renewalDate?: number;
    pendingChange?: boolean;
    cancelScheduled?: boolean;
    resume?: boolean;
    needsReview?: boolean;
    paymentUrl?: string;
    canUndoChange?: boolean;
    plans?: { periodMonths: number; productId: string; amount?: number; currency?: string; isCurrent?: boolean }[];
    quote?: string;
    signature?: string;
    deferred?: boolean;
    effectiveDate?: number;
    amountDue?: number;
    unusedCredit?: number;
    renewalAmount?: number;
    currency?: string;
    status?: "applied" | "scheduled" | "payment_required" | "unchanged" | "reconciliation_required";
    url?: string;
};

export type CancelRetentionOfferResponse = {
    eligible: boolean
    reason: string
    ineligibleReason: string | null
    discountPercent: number
    offerId: string
    canRedeemInClient: boolean
    platform?: string
    googleOfferId?: string
    appleOfferId?: string
    stripeCouponId?: string
    subscriptionId?: string
    productId?: string | null
    periodMonths?: number | null
    expirationDate?: number
    currentAmount?: number
    discountedAmount?: number
    currency?: string
}

export type AcceptCancelRetentionOfferResponse = {
    status: string
    applied: boolean
    platform?: string
}

export type CancelGoogleSubscriptionResponse = {
    status: string
    expirationDate: number
    alreadyCancelled?: boolean
}

export type GetTrialTimeResponse = {
    trialTimeMinutes: number
}

export type GetAgentsResponse = {
    agents: Agent[]
}

export type GetAgentMessagesResponse = {
    sessions: Message[][]
    sessionIds: string[]
}
export type GetAgentHistoryIndexResponse = {
    agentId: string,
    sessionIds: string[],
    sessions: HistorySessionSummary[],
    etag: string,
    unchanged: false
} | {
    agentId: string,
    etag: string,
    unchanged: true
}
export type GetSessionMessagesResponse = {
    sessionId: string,
    messages: Message[],
    etag: string,
    unchanged: false
} | {
    sessionId: string,
    etag: string,
    unchanged: true
}
export type UpdateSessionTitleResponse = {
    title: string
}
export type GetLanguageSettingsResponse = {
    languageSettings: {
        id: string;
        language: string;
        proficiencyLevel: string;
        formalness: string;
        grammarCorrection: string | null;
        tutorLanguageUsage: string | null;
        savedWordsPracticeStyles: string[];
        finishedIntroLesson: boolean;
        languageGoal1: string | null;
        languageGoal2: string | null;
        interest1: string | null;
        interest2: string | null;
        introPrompt: string | null;
        conversationPrompt: string | null;
        rubyInChat: boolean;
        transliterationStyle: string | null;
        flashcardAudioAgentTemplateId: string | null;
    },
    studyStats: StudyStats,
    historicalStats: HistoricalStats
}

export type UpdateLanguageSettingsResponse = {
    languageSettings: {
        id: string;
        language: string;
        proficiencyLevel: string;
        formalness: string;
        grammarCorrection: string;
        tutorLanguageUsage: string;
        savedWordsPracticeStyles: string[];
    }
}

export type Kickstart = {
    id: string,
    description: string,
    prompt: string,
    type: string,
    category?: string,
    subcategory?: string,
    custom_user_role?: string,
    custom_tutor_role?: string,
    custom_additional_details?: string,
    custom_structured_tutor_messages?: string[]
}


export type LocalizedKickstart = {
    kickstart: Kickstart,
    language: string,
    localized_description: string
}

export type CreateCustomScenarioKickstartResponse = LocalizedKickstart;
export type UpdateCustomScenarioKickstartResponse = LocalizedKickstart;

function buildLocalizedKickstart(entry: any, language: string): LocalizedKickstart {
    const ld: string = entry.ld;
    const kickstart: Kickstart = { id: entry.id, description: ld, prompt: "", type: entry.type };
    if (entry.category !== undefined) kickstart.category = entry.category;
    if (entry.subcategory !== undefined) kickstart.subcategory = entry.subcategory;
    if (entry.custom_user_role !== undefined) kickstart.custom_user_role = entry.custom_user_role;
    if (entry.custom_tutor_role !== undefined) kickstart.custom_tutor_role = entry.custom_tutor_role;
    if (entry.custom_additional_details !== undefined) {
        kickstart.custom_additional_details = entry.custom_additional_details;
    }
    if (Array.isArray(entry.custom_structured_tutor_messages)) {
        kickstart.custom_structured_tutor_messages = entry.custom_structured_tutor_messages;
    }
    return { kickstart, language, localized_description: ld };
}

function denormalizeKickstarts(raw: any): GetLocalizedKickstartsResponse {
    if (!Array.isArray(raw?.entries)) return raw as GetLocalizedKickstartsResponse;
    const language: string = raw.kickstartLanguage ?? '';
    const built: LocalizedKickstart[] = raw.entries.map((entry: any) => buildLocalizedKickstart(entry, language));
    const list = (key: string): LocalizedKickstart[] =>
        Array.isArray(raw[key]) ? raw[key].map((i: number) => built[i]) : [];
    const map = (key: string): Record<string, LocalizedKickstart[]> => {
        const out: Record<string, LocalizedKickstart[]> = {};
        const m = raw[key];
        if (m !== null && typeof m === 'object') {
            for (const category of Object.keys(m)) out[category] = m[category].map((i: number) => built[i]);
        }
        return out;
    };
    return {
        studentLifeKickstarts: list('studentLifeKickstarts'),
        tutorLifeKickstarts: list('tutorLifeKickstarts'),
        topicKickstarts: list('topicKickstarts'),
        scenarioKickstarts: list('scenarioKickstarts'),
        grammarKickstarts: list('grammarKickstarts'),
        savedWordsKickstarts: list('savedWordsKickstarts'),
        proficiencyGrammarKickstarts: map('proficiencyGrammarKickstarts'),
        categoryTopicKickstarts: map('categoryTopicKickstarts'),
        categoryScenarioKickstarts: map('categoryScenarioKickstarts'),
        categoryStudentLifeKickstarts: map('categoryStudentLifeKickstarts'),
        categoryTutorLifeKickstarts: map('categoryTutorLifeKickstarts'),
        completedKickstartIds: Array.isArray(raw.completedKickstartIds) ? raw.completedKickstartIds : [],
    } as GetLocalizedKickstartsResponse;
}

export type GetLocalizedKickstartsResponse = {
    dailyKickstart: {
        kickstart: LocalizedKickstart,
        completed: boolean
    },
    studentLifeKickstarts: LocalizedKickstart[],
    tutorLifeKickstarts: LocalizedKickstart[],
    topicKickstarts: LocalizedKickstart[],
    scenarioKickstarts: LocalizedKickstart[],
    grammarKickstarts: LocalizedKickstart[],
    savedWordsKickstarts: LocalizedKickstart[],
  proficiencyGrammarKickstarts?: Record<string, LocalizedKickstart[]>,
    categoryTopicKickstarts?: Record<string, LocalizedKickstart[]>,
    categoryScenarioKickstarts?: Record<string, LocalizedKickstart[]>,
    categoryStudentLifeKickstarts?: Record<string, LocalizedKickstart[]>,
    categoryTutorLifeKickstarts?: Record<string, LocalizedKickstart[]>,
    completedKickstartIds?: string[]
}
export type CorrectMessageResponse = {
    correctionSummary: string,
    score: number
}

export type DeleteUserResponse = {}

export type FlagMessageResponse = {}

export type AddWordResponse = {
    id: number
}

export type GetWordsResponse = {
    words: Word[],
    cards: Card[]
}

export type GetWordResponse = {
    word: Word
}

export type DeleteWordResponse = {}

export type DeleteAllWordsResponse = {}

export type ResetPasswordResponse = {
}
export type GetDueCardsResponse = {
    cards: Card[],
    words: Word[],
    doneReviewsToday: number,
    reviewOrderDayStart?: number,
}
export type RateCardResponse = {
    card: Card,
    review: Review
}   

export type SetCardsEnabledResponse = {}

export type SetAllCardsEnabledResponse = {}

export type GetCardsByWordIdResponse = {
    cards: Card[]
}
export type FlashcardRetentionBucketStats = {
    passed: number,
    total: number
}

export type FlashcardRetentionPeriodStats = {
    young: FlashcardRetentionBucketStats,
    mature: FlashcardRetentionBucketStats,
    total: FlashcardRetentionBucketStats
}

export type FlashcardRetentionPeriodKey = 'today' | 'yesterday' | 'lastWeek' | 'lastMonth' | 'lastYear'

export type FlashcardCardCounts = {
    new: number,
    learning: number,
    relearning: number,
    young: number,
    mature: number,
    total: number
}

export type GetFlashcardStatsResponse = {
    retention: Record<FlashcardRetentionPeriodKey, FlashcardRetentionPeriodStats>,
    cardCounts: FlashcardCardCounts,
    dailyAdded: Record<string, number>,
    dailyReviews: Record<string, number>,
    dailyStudySeconds: Record<string, number>,
    totalReviews: number,
    totalStudySeconds: number,
    averageAnswerSeconds?: number,
    dailyAnswerSeconds?: Record<string, number>,
    matureIntervalDays: number
}
export type UpdateWordResponse = {}

export type ManageWordAudioSlot = 'word' | 'sentence' | 'shared_example'
export type ManageWordAudioAction = 'generate' | 'regenerate' | 'remove'
export type ManageWordAudioResponse = Word

export type AddWordEntry = {
    word: string,
    sentence?: string
}
export type AddWordsResponse = {}

export type DeleteReviewResponse = {
    success: boolean,
    cardId: number
}

export type AttachRedirectAttributionResponse = {
    success: boolean,
    attached: boolean,
    campaignSlug: string,
    platform: string,
}

export type GetStoreRatingResponse = {
    averageUserRating: number
}

export type TranslateWordsResponse = {
    translatedContent: {
        content: string;
        isWord: boolean;
        definition: string;
    }[]
}

export type GetPhraseDefinitionResponse = {
    definition: string
}

export type GetSessionsResponse = {
    sessions: Session[]
}

export type { Session };

export type Subunit = {
    description: string;
    position: number;
    kickstart_id?: string;
    kickstart_type?: string;
    completed: boolean;
    is_review: boolean;
    unit_id: string;
    subunit_id: string;
}

export type Unit = {
    description: string;
    position: number;
    unit_id: string;
    subunits: Subunit[];
}


export type GetCurriculumResponse = {
    units: Unit[];
    localizedKickstarts: Record<string, string>;
}

export type StartOnboardingCurriculumGenerationResponse = {
    targetUnitCount: number;
    targetSubunitCount: number;
}

export type StartOnboardingCurriculumGenerationPayload = {
    language: string;
    interest1: string | null;
    interest2: string | null;
}

export type RegenerateCurriculumResponse = {}

export type GetCurriculumGenerationStatusResponse = {
    inProgress: boolean;
    generationStartedAt: number;
    generationCompletedAt?: number;
    serverTimeSeconds: number;
    unitCount: number;
    subunitCount: number;
    targetUnitCount: number;
    targetSubunitCount: number;
}

export type RegenerateCurrentLessonResponse = {
    success: boolean;
    description: string;
    kickstartId: string;
    kickstartType: string;
    localizedDescription: string;
}

export type ExportFlashcardsCSVResponse = {
    csvData: string
}

export type GetStreakResponse = {
    currentStreak: number;
    maxStreak: number;
    streakIncreasedToday: boolean;
    // Optional: older servers do not return it.
    streakScreenShownToday?: boolean;
    dailyStats: {
        [date: string]: {
            call_seconds: number;
            flashcard_seconds: number;
            review_count: number;
        }
    }
}

export type ClaimStreakScreenResponse = {
    shouldShow: boolean;
}

// Endpoints that answer change checks: the last full response is kept in
// IndexedDB and reused when the server says it is unchanged.
const CONDITIONAL_ENDPOINTS = new Set<string>([
    '/get_words',
    '/get_sessions',
    '/get_curriculum',
    '/get_localized_kickstarts',
    '/get_available_agent_templates',
]);

type ConditionalRequest = {
    key: string;
    generation: number;
    cached: ConditionalResponse | null;
    data: Record<string, unknown>;
};

class PracticeAPI {
    domainPort: string;
    private currentUserId: string | null = null;
    private readonly sessionStartOwnership: SessionStartOwnership;
    private readonly sessionStartsInFlight = new Map<string, number>();
    private readonly replayedSessionStarts = new Set<string>();

    constructor(domainPort: string, sessionStartStorage?: Storage | null) {
        this.domainPort = domainPort;
        const storage = sessionStartStorage === undefined
            ? (typeof window === 'undefined' ? null : window.sessionStorage)
            : sessionStartStorage;
        this.sessionStartOwnership = new SessionStartOwnership(storage);
    }

    private async conditionalRequestFor(url: string, inputData: Object): Promise<ConditionalRequest | null> {
        const body = inputData as Record<string, unknown>;
        if (this.currentUserId === null || 'ifNoneMatch' in body) {
            return null;
        }
        // Only the full saved-words list carries an etag; deltas are small.
        if (url === '/get_words' && (body.addedAfter !== undefined || body.sessionId !== undefined)) {
            return null;
        }
        const key = ConditionalResponseCache.keyFor(this.currentUserId, url, body);
        const generation = conditionalResponseCache.currentGeneration();
        const cached = await conditionalResponseCache.lookup(key);
        return { key, generation, cached, data: { ...body, ifNoneMatch: cached?.etag ?? null } };
    }

    private clearConditionalResponses(): void {
        this.currentUserId = null;
        void conditionalResponseCache.clear();
    }

    private async handleAPICall(url: string, inputData: Object, outputData: Ref, isFetching: Ref<boolean>, errorOut: Ref<number | null>, 
        responseType?: ResponseType, method: string = 'post', httpErrorOut?: Ref<number | null>, timeoutMs?: number): Promise<void> {
        isFetching.value = true;
        errorOut.value = null;
        const conditional = method === 'post' && CONDITIONAL_ENDPOINTS.has(url)
            ? await this.conditionalRequestFor(url, inputData)
            : null;
        const config: AxiosRequestConfig = {
            method: method,
            url: this.domainPort + url,
            ...(method === 'post' ? { data: conditional?.data ?? inputData } : {}),
            responseType,
            timeout: timeoutMs,
            // withCredentials: true,
            headers: {
                'Client-Platform': 'web',
                'Client-Version': '1.16.10'
            }
        }
        return axios(config).then(async (response) =>  {
            if (LOG_SENSITIVE_DATA) {
                console.log(`${method.toUpperCase()} /`, url, " response: ", response);
            }
            const payload: unknown = response.data;
            if (conditional !== null && payload !== null && typeof payload === 'object') {
                const answer = payload as { etag?: unknown; unchanged?: unknown };
                if (answer.unchanged === true) {
                    if (conditional.cached === null || answer.etag !== conditional.cached.etag) {
                        // Only a stored etag is ever sent, so the server and the
                        // cache disagree: fetch the full response instead.
                        return this.handleAPICall(url, { ...conditional.data, ifNoneMatch: null }, outputData, isFetching,
                            errorOut, responseType, method, httpErrorOut, timeoutMs);
                    }
                    outputData.value = structuredClone(conditional.cached.data);
                    isFetching.value = false;
                    return;
                }
                if (typeof answer.etag === 'string') {
                    void conditionalResponseCache.store(
                        { key: conditional.key, etag: answer.etag, data: structuredClone(payload) },
                        conditional.generation,
                    );
                }
            }
            if (url.startsWith('/get_user') && payload !== null && typeof payload === 'object') {
                const userId = (payload as { user?: { userId?: unknown } }).user?.userId;
                if (typeof userId === 'string' && userId.length > 0) {
                    this.currentUserId = userId;
                }
            }
            if (!response.data) {
                outputData.value = {};
            } else {
                outputData.value = response.data; //@ts-ignore
            }
            isFetching.value = false;
        }).catch(async (error: AxiosError) => {
            if (LOG_SENSITIVE_DATA) {
                console.log(`${method.toUpperCase()} /`, url, " error: ", error.response, error.message);
            } else {
                console.warn(
                    `${method.toUpperCase()} / ${url} failed with status ${error.response?.status ?? 'network_error'}`,
                );
            }
            if (error.response) {
                if (httpErrorOut && typeof error.response.status === 'number') {
                    httpErrorOut.value = error.response.status;
                }
                if (error.response.data !== null && typeof error.response.data === 'object') {
                    const responseError = (error.response.data as { error?: unknown }).error;
                    if (typeof responseError === 'number') {
                        errorOut.value = responseError;
                    } else if (error.response.status === 429) {
                        errorOut.value = 80;
                    } else {
                        errorOut.value = 0;
                    }
                } else if (error.response.status === 429) {
                    errorOut.value = 80;
                } else {
                    errorOut.value = 0;
                }
            } else {
                errorOut.value = 0;
            }
            isFetching.value = false;
            if (
                error.response?.status === 401
                && !isExpectedUnauthenticatedUrl(url)
                && consumeAuthenticationFailure()
            ) {
                try {
                    this.clearSessionStartOwnership();
                    clearHistoryCache();
                    this.clearConditionalResponses();
                    await clearSavedWordsCache();
                } catch {
                    // Cache cleanup must never block revoked-session recovery.
                } finally {
                    notifyAuthenticationFailure();
                }
            }
        });
    }

    private async handleAPICallWithRetries(
        url: string, 
        inputData: Object, 
        outputData: Ref, 
        isFetching: Ref<boolean>, 
        errorOut: Ref<number | null>, 
        responseType?: ResponseType, 
        method: string = 'post',
        retryOnErrorCodes: number[] = [],
        retryOnHttpStatusCodes?: number[],
        maxRetries: number = 0,
        httpStatusOut?: Ref<number | null>,
    ): Promise<void> {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            const httpErrorOut = ref<number | null>(null);
            errorOut.value = null;
            await this.handleAPICall(url, inputData, outputData, isFetching, errorOut, responseType, method, httpErrorOut);
            if (httpStatusOut !== undefined) {
                httpStatusOut.value = httpErrorOut.value;
            }

            const httpCodes = retryOnHttpStatusCodes ?? [];
            const shouldRetryHttp = httpErrorOut.value !== null && httpCodes.includes(httpErrorOut.value);
            const shouldRetryApp = errorOut.value !== null && retryOnErrorCodes.includes(errorOut.value);
            // If successful or not a retryable error, return
            if (errorOut.value === null && !shouldRetryHttp) {
                return;
            }
            if (!shouldRetryHttp && !shouldRetryApp) {
                return;
            }
            
            // If this was the last attempt, break to return the failed response
            if (attempt === maxRetries) {
                if (shouldRetryHttp) {
                    errorOut.value = httpErrorOut.value;
                }
                break;
            }
            
            // Brief wait before retrying
            await new Promise(resolve => setTimeout(resolve, 50));
            console.log(`Retrying ${url}, attempt ${attempt + 2}/${maxRetries + 1} after ${(shouldRetryHttp ? 'HTTP ' + httpErrorOut.value : errorOut.value)} error`);
        }
    }

    async checkUsername(username: string, result: ApiRes<CheckUsernameResponse>) : Promise<void> {
        return this.handleAPICall("/check_username", {username}, result.data, result.isFetching, result.error);
    }
    async create_user(username: string, password: string, timezone: string, name: string, redirectClickId: string | null, result: ApiRes<CreateUserResponse>) : Promise<void> {
        const prehashed = await sha256(password);
        return this.handleAPICall("/create_user", {username, prehashed, timezone, name, redirectClickId}, result.data, result.isFetching, result.error);
    }
    async login(username: string, password: string, result: ApiRes<LoginResponse>) : Promise<void> {
        const prehashed = await sha256(password);
        await this.handleAPICall("/login", {username, prehashed}, result.data, result.isFetching, result.error);
        if (result.error.value === null && result.data.value?.success === true) {
            markAuthenticatedSession();
        }
    }
    async update_user(result: ApiRes<UpdateUserResponse>, partialUserSettings: Partial<UserSettings>): Promise<void> {
        const updateData: Partial<UserSettings> = {};
        for (const [key, value] of Object.entries(partialUserSettings)) {
            if (value !== undefined) {
                //@ts-ignore
                updateData[key as keyof UserSettings] = value;
            }
        }
        return this.handleAPICall("/update_user", updateData, result.data, result.isFetching, result.error);
    }
    async attachRedirectAttribution(redirectClickId: string, result: ApiRes<AttachRedirectAttributionResponse>): Promise<void> {
        return this.handleAPICall(
            "/attach_redirect_attribution",
            { redirect_click_id: redirectClickId },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async deleteUser(prehashed: string, result: ApiRes<DeleteUserResponse>) : Promise<void> {
        await this.handleAPICall("/delete_user", {prehashed}, result.data, result.isFetching, result.error);
        if (result.error.value === null) {
            clearAuthenticatedSession();
            this.clearSessionStartOwnership();
            clearHistoryCache();
            this.clearConditionalResponses();
            await clearSavedWordsCache();
        }
    }
    
    async logout(result: ApiRes<LogoutResponse>) : Promise<void> {
        await this.handleAPICallWithRetries(
            "/logout",
            {},
            result.data,
            result.isFetching,
            result.error,
            undefined,
            "post",
            [0],
            [429, 500, 502, 503, 504],
            2,
        );
        if (result.error.value === null) {
            clearAuthenticatedSession();
            this.clearSessionStartOwnership();
            clearHistoryCache();
            this.clearConditionalResponses();
            await clearSavedWordsCache();
        }
    }

    async getUser(result: ApiRes<GetUserResponse>) : Promise<void> {
        await this.getUserWithSubscriptionReconciliation(result, false);
    }
    async getUserForSubscriptionCheckout(result: ApiRes<GetUserResponse>) : Promise<void> {
        await this.getUserWithSubscriptionReconciliation(result, true);
    }
    private async getUserWithSubscriptionReconciliation(
        result: ApiRes<GetUserResponse>,
        reconcileSubscription: boolean,
    ): Promise<void> {
        const url = reconcileSubscription
            ? "/get_user?reconcileSubscription=true"
            : "/get_user";
        await this.handleAPICall(
            url,
            {},
            result.data,
            result.isFetching,
            result.error,
        );
        if (result.error.value === null && result.data.value?.user !== undefined) {
            markAuthenticatedSession();
        }
    }
    async getAvailableAgentTemplates(result: ApiRes<GetAvailableAgentTemplatesResponse>) : Promise<void> {
        return this.handleAPICall("/get_available_agent_templates", { includeExperimentalGemini: true }, result.data, result.isFetching, result.error);
    }
    async checkSentence(sentence: string, previousMessages: Message[], result: ApiRes<CheckSentenceResponse>) : Promise<void> {
        return this.handleAPICall("/check_sentence", {sentence, previousMessages}, result.data, result.isFetching, result.error);
    }
    async getLeaderboard(result: ApiRes<GetLeaderboardResponse>) : Promise<void> {
        return this.handleAPICall("/get_leaderboard", {}, result.data, result.isFetching, result.error);
    }
    async getUserUsage(result: ApiRes<GetUserUsageResponse>) : Promise<void> {
        return this.handleAPICall("/get_user_usage", {}, result.data, result.isFetching, result.error);
    }
    async getSubscriptionDetails(result: ApiRes<GetSubscriptionDetailsResponse>) : Promise<void> {
        return this.handleAPICall("/get_subscription_details", {}, result.data, result.isFetching, result.error);
    }
    async startSession(sessionType: SessionType, result: ApiRes<StartSessionResponse>): Promise<void> {
        const request: StartSessionRequest = {
            deviceType: 'web',
            sessionType,
            sessionRequestId: randomUuid().replace(/-/g, ''),
            requiredMediaProvider: 'livekit',
        };
        return this.handleAPICall("/start_session", request, result.data, result.isFetching, result.error);
    }

    async reportMediaTelemetry(
        sessionId: string,
        payload: ClientMediaTelemetryPayload,
    ): Promise<void> {
        const url = `${this.domainPort}/report_media_telemetry/${encodeURIComponent(sessionId)}`;
        const serializedPayload = JSON.stringify(payload);
        if (
            typeof navigator.sendBeacon === 'function'
            && navigator.sendBeacon(url, serializedPayload)
        ) {
            return;
        }
        await axios({
            method: 'post',
            url,
            data: serializedPayload,
            timeout: 1500,
            headers: {
                'Client-Platform': 'web',
                'Client-Version': '1.16.10',
                'Content-Type': 'application/json',
            },
        });
    }

    async startSessionWithRetries(
        sessionType: SessionType,
        result: ApiRes<StartSessionResponse>,
        ownerUserId: string,
    ): Promise<void> {
        let attempt: SessionStartAttempt | null = this.sessionStartOwnership.begin(
            ownerUserId,
            sessionType,
        );
        let sessionRequestId = attempt.requestId;
        let lastHttpStatus: number | null = null;
        const postStartSession = async (
            currentAttempt: SessionStartAttempt | null,
            requestId: string,
        ): Promise<void> => {
            const request: StartSessionRequest = {
                deviceType: 'web',
                sessionType,
                sessionRequestId: requestId,
                requiredMediaProvider: 'livekit',
            };
            if (currentAttempt !== null && currentAttempt.supersedesSessionId !== undefined) {
                request.supersedesSessionId = currentAttempt.supersedesSessionId;
                if (currentAttempt.sessionOwnershipToken !== undefined) {
                    request.sessionOwnershipToken = currentAttempt.sessionOwnershipToken;
                }
            }
            // A start that reuses the request id of one still in flight gets the
            // same session back, so the earlier caller must not release it.
            const inFlight = this.sessionStartsInFlight.get(requestId) ?? 0;
            if (inFlight > 0) {
                this.replayedSessionStarts.add(requestId);
            }
            this.sessionStartsInFlight.set(requestId, inFlight + 1);
            const httpStatus = ref<number | null>(null);
            try {
                await this.handleAPICallWithRetries(
                    "/start_session",
                    request,
                    result.data,
                    result.isFetching,
                    result.error,
                    undefined,
                    'post',
                    [],
                    [503],
                    1,
                    httpStatus,
                );
            } finally {
                const remaining = (this.sessionStartsInFlight.get(requestId) ?? 1) - 1;
                if (remaining > 0) {
                    this.sessionStartsInFlight.set(requestId, remaining);
                } else {
                    this.sessionStartsInFlight.delete(requestId);
                }
            }
            lastHttpStatus = httpStatus.value;
        };
        await postStartSession(attempt, sessionRequestId);
        if (lastHttpStatus === 409) {
            this.sessionStartOwnership.clear();
            attempt = this.sessionStartOwnership.begin(ownerUserId, sessionType);
            sessionRequestId = attempt.requestId;
            result.data.value = null;
            await postStartSession(attempt, sessionRequestId);
        }
        const started = result.data.value;
        if (result.error.value !== null || started === null) {
            return;
        }
        if (
            attempt !== null
            && typeof started.sessionId === 'string'
            && started.sessionId.length > 0
            && typeof started.sessionOwnershipToken === 'string'
            && started.sessionOwnershipToken.length > 0
        ) {
            this.sessionStartOwnership.confirm(
                attempt,
                ownerUserId,
                sessionType,
                started.sessionId,
                started.sessionOwnershipToken,
            );
        }
    }

    clearSessionStartOwnership(): void {
        this.sessionStartOwnership.clear();
        // Callers clear ownership only after abandoning every start in flight.
        this.replayedSessionStarts.clear();
    }

    // The server counts a started session toward the user's active-call limit
    // until someone joins it or its join wait expires (about two minutes). When
    // the page drops a start before joining, a final report without a join
    // cancels it. A newer start that replayed the same request keeps it. One
    // that supersedes it gets a new session, and superseding a cancelled
    // session is allowed.
    async releaseUnusedSessionStart(started: StartSessionResponse): Promise<void> {
        if (this.replayedSessionStarts.delete(started.sessionId)) {
            return;
        }
        const telemetry = new ClientMediaTelemetryTracker(
            getMediaSessionCredentials(started).provider,
            (sessionId, payload) => this.reportMediaTelemetry(sessionId, payload),
        );
        telemetry.start(started.sessionId);
        telemetry.markIntentionalDisconnect();
        await telemetry.report(async () => EMPTY_NETWORK_SNAPSHOT);
    }

    async startSessionPreflight(
        sessionType: SessionType,
        result: ApiRes<StartSessionPreflightResponse>,
    ): Promise<void> {
        result.isFetching.value = true;
        result.data.value = null;
        result.error.value = null;
        try {
            const response = await axios<StartSessionPreflightResponse>({
                method: 'post',
                url: `${this.domainPort}/preflight_session`,
                data: {
                    deviceType: 'web',
                    sessionType,
                    requiredMediaProvider: 'livekit',
                },
                timeout: START_SESSION_PREFLIGHT_TIMEOUT_MS,
                headers: {
                    'Client-Platform': 'web',
                    'Client-Version': '1.16.10',
                },
            });
            result.data.value = response.data;
        } catch (error: unknown) {
            const axiosError = error as AxiosError<{ error?: unknown }>;
            const status = axiosError.response?.status;
            if (status === undefined || status === 404 || status >= 500) {
                result.data.value = { eligible: true };
                return;
            }
            const responseError = axiosError.response?.data?.error;
            result.error.value = typeof responseError === 'number'
                ? responseError
                : 0;
        } finally {
            result.isFetching.value = false;
        }
        if (result.data.value === null && result.error.value === null) {
            result.data.value = { eligible: true };
        }
    }
    async getPrices(result: ApiRes<GetPricesResponse>): Promise<void> {
        return this.handleAPICall("/get_prices", {}, result.data, result.isFetching, result.error);
    }
    async getPaywallCopyConfig(source: string, result: ApiRes<GetPaywallCopyConfigResponse>): Promise<void> {
        return this.handleAPICall(
            "/get_paywall_copy_config",
            { source },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async recordPaywallCopyEvent(eventType: "continued" | "checkout", result: ApiRes<Record<string, never>>): Promise<void> {
        return this.handleAPICall(
            "/record_paywall_copy_event",
            { eventType },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async getPrice(result: ApiRes<GetPriceResponse>, priceId: string): Promise<void> {
        return this.handleAPICall("/get_price", { priceId }, result.data, result.isFetching, result.error);
    }
    async subscribe(priceId: string, result: ApiRes<SubscribeResponse>, source: string, tracking: SubscribeTrackingPayload | null, currency: string | null): Promise<void> {
        const payload = tracking === null
            ? { priceId, source, currency }
            : { priceId, source, ...tracking, currency };
        return this.handleAPICall("/subscribe", payload, result.data, result.isFetching, result.error);
    }
    async trackPaidSocialEvent(payload: TrackPaidSocialEventPayload, result: ApiRes<Record<string, never>>): Promise<void> {
        return this.handleAPICall("/track_attribution_event", payload, result.data, result.isFetching, result.error);
    }
    async createStripePortalSession(
        result: ApiRes<CreateStripePortalSessionResponse>,
        cancel: boolean,
        includeRetentionOffer: boolean,
    ): Promise<void> {
        return this.handleAPICall(
            "/create_stripe_portal_session",
            { cancel, includeRetentionOffer },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async changeSubscriptionPlan(result: ApiRes<PlanChangeResponse>, data: Record<string, unknown>): Promise<void> {
        return this.handleAPICall("/change_subscription_plan", data, result.data, result.isFetching, result.error);
    }
    async lifetimeUpgrade(result: ApiRes<LifetimeUpgradeResponse>, data: Record<string, unknown>): Promise<void> {
        return this.handleAPICall("/lifetime_upgrade", data, result.data, result.isFetching, result.error);
    }
    async getCancelRetentionOffer(result: ApiRes<CancelRetentionOfferResponse>): Promise<void> {
        return this.handleAPICall("/get_cancel_retention_offer", {}, result.data, result.isFetching, result.error);
    }
    async acceptCancelRetentionOffer(result: ApiRes<AcceptCancelRetentionOfferResponse>): Promise<void> {
        return this.handleAPICall("/accept_cancel_retention_offer", {}, result.data, result.isFetching, result.error);
    }
    async cancelGoogleSubscription(result: ApiRes<CancelGoogleSubscriptionResponse>): Promise<void> {
        return this.handleAPICall("/cancel_google_subscription", {}, result.data, result.isFetching, result.error);
    }
    async getAgents(result: ApiRes<GetAgentsResponse>, sorted: boolean = true): Promise<void> {
        return this.handleAPICall("/get_agents", { sorted }, result.data, result.isFetching, result.error);
    }
    async getAgentsIncludingHidden(result: ApiRes<GetAgentsResponse>): Promise<void> {
        return this.handleAPICall(
            "/get_agents",
            { sorted: true, includeHidden: true },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async hideAgentTemplate(result: ApiRes<HideAgentTemplateResponse>, agentTemplateId: string): Promise<void> {
        return this.handleAPICall(
            "/hide_agent_template",
            { agentTemplateId },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async updateAgentCustomInstructions(result: ApiRes<UpdateAgentCustomInstructionsResponse>, agentId: string, customInstructions: string | null): Promise<void> {
        return this.handleAPICall("/update_agent_custom_instructions", { agentId, customInstructions }, result.data, result.isFetching, result.error);
    }
    async getAgentMessages(result: ApiRes<GetAgentMessagesResponse>, agentId: string) : Promise<void> {
        return this.handleAPICall("/get_agent_messages", {agentId}, result.data, result.isFetching, result.error);
    }
    async getAgentHistoryIndex(
        result: ApiRes<GetAgentHistoryIndexResponse>,
        agentId: string,
        ifNoneMatch: string | null,
    ): Promise<void> {
        const payload: { agentId: string; ifNoneMatch?: string } = { agentId };
        if (ifNoneMatch !== null) {
            payload.ifNoneMatch = ifNoneMatch;
        }
        return this.handleAPICall(
            "/get_agent_history_index",
            payload,
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async getSessionMessages(
        result: ApiRes<GetSessionMessagesResponse>,
        agentId: string,
        sessionId: string,
        ifNoneMatch: string | null,
    ): Promise<void> {
        const payload: { agentId: string; sessionId: string; ifNoneMatch?: string } = {
            agentId,
            sessionId,
        };
        if (ifNoneMatch !== null) {
            payload.ifNoneMatch = ifNoneMatch;
        }
        return this.handleAPICall(
            "/get_session_messages",
            payload,
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async updateSessionTitle(
        result: ApiRes<UpdateSessionTitleResponse>,
        sessionId: string,
        title: string,
    ): Promise<void> {
        return this.handleAPICall(
            "/update_session_title",
            { sessionId, title },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async getLanguageSettings(result: ApiRes<GetLanguageSettingsResponse>, historical: boolean = false): Promise<void> {
        return this.handleAPICall("/get_language_settings", {historical}, result.data, result.isFetching, result.error);
    }
    async getLanguageSettingsForLanguage(language: string, result: ApiRes<GetLanguageSettingsResponse>): Promise<void> {
        return this.handleAPICall("/get_language_settings_for_language", {language: language}, result.data, result.isFetching, result.error);
    }
    async createLanguageSettingsForLanguage(language: string, result: ApiRes<GetLanguageSettingsResponse>): Promise<void> {
        return this.handleAPICall("/create_language_settings_for_language", {language: language}, result.data, result.isFetching, result.error);
    }
    async updateLanguageSettings(result: ApiRes<UpdateLanguageSettingsResponse>, data: {
        language: string;
        formalness?: string;
        proficiencyLevel?: string;
        tutorLanguageUsage?: string;
        grammarCorrection?: string;
        languageGoal1?: string;
        languageGoal2?: string;
        savedWordsPracticeStyles?: string[];
        rubyInChat?: boolean;
        transliterationStyle?: string | null;
        flashcardAudioAgentTemplateId?: string | null;
    }): Promise<void> {
        return this.handleAPICall("/update_language_settings", data, result.data, result.isFetching, result.error);
    }
    async getLocalizedKickstarts(result: ApiRes<GetLocalizedKickstartsResponse>): Promise<void> {
        const raw: Ref<any> = ref(null);
        await this.handleAPICall("/get_localized_kickstarts", {}, raw, result.isFetching, result.error);
        if (raw.value !== null && raw.value !== undefined) {
            result.data.value = denormalizeKickstarts(raw.value);
        }
    }
    async createCustomScenarioKickstart(
        userRole: string,
        tutorRole: string,
        additionalDetails: string,
        structuredTutorMessages: string[] | null,
        result: ApiRes<CreateCustomScenarioKickstartResponse>,
    ): Promise<void> {
        return this.handleAPICall(
            "/create_custom_scenario_kickstart",
            { userRole, tutorRole, additionalDetails, structuredTutorMessages },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async deleteCustomScenarioKickstart(
        kickstartId: string,
        result: ApiRes<Record<string, never>>,
    ): Promise<void> {
        return this.handleAPICall(
            "/delete_custom_scenario_kickstart",
            { kickstartId },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async updateCustomScenarioKickstart(
        kickstartId: string,
        userRole: string,
        tutorRole: string,
        additionalDetails: string,
        structuredTutorMessages: string[] | null,
        result: ApiRes<UpdateCustomScenarioKickstartResponse>,
    ): Promise<void> {
        return this.handleAPICall(
            "/update_custom_scenario_kickstart",
            { kickstartId, userRole, tutorRole, additionalDetails, structuredTutorMessages },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async correctMessage(previousMessages: string[], message: string, language: string, result: ApiRes<CorrectMessageResponse>): Promise<void> {
        return this.handleAPICall("/correct_message", {previousMessages, message, language}, result.data, result.isFetching, result.error);
    }
    async flagMessage(messageId: string, result: ApiRes<FlagMessageResponse>): Promise<void> {
        return this.handleAPICall("/flag_message", {messageId}, result.data, result.isFetching, result.error);
    }
    async addWord(messageId: string, word: string, occurranceIndex: number, language: string, result: ApiRes<AddWordResponse>): Promise<void> {
        return this.handleAPICall("/add_word", {messageId, word, occurranceIndex, language}, result.data, result.isFetching, result.error);
    }
    async addWordFlexible(word: string, language: string, result: ApiRes<AddWordResponse>, opts: { sentence?: string }): Promise<void> {
        const payload: { word: string; language: string; sentence?: string } = { word, language };
        if (opts.sentence !== undefined && opts.sentence.trim().length > 0) {
            payload.sentence = opts.sentence;
        }
        return this.handleAPICall("/add_word", payload, result.data, result.isFetching, result.error);
    }
    async getWords(getDefinitions: boolean, language: string, result: ApiRes<GetWordsResponse>, addedAfter?: number, sessionId?: string): Promise<void> {
        const data: { getDefinitions: boolean; language: string; addedAfter?: number; sessionId?: string } = { getDefinitions, language };
        if (addedAfter !== undefined) {
            data.addedAfter = addedAfter;
        }
        if (sessionId !== undefined) {
            data.sessionId = sessionId;
        }
        return this.handleAPICall("/get_words", data, result.data, result.isFetching, result.error);
    }
    async getWord(wordId: number, result: ApiRes<GetWordResponse>): Promise<void> {
        return this.handleAPICall("/get_word", {wordId}, result.data, result.isFetching, result.error);
    }
    async deleteWord(wordId: number, result: ApiRes<DeleteWordResponse>, sessionId?: string): Promise<void> {
        return this.handleAPICall("/delete_word", {wordId, sessionId}, result.data, result.isFetching, result.error);
    }
    async deleteAllWords(result: ApiRes<DeleteAllWordsResponse>): Promise<void> {
        return this.handleAPICall("/delete_all_words", {}, result.data, result.isFetching, result.error);
    }
    async resetPassword(email: string, result: ApiRes<ResetPasswordResponse>): Promise<void> {
        return this.handleAPICall("/reset_password", {email}, result.data, result.isFetching, result.error);
    }
    async loginWithCode(email: string, code: string, result: ApiRes<LoginResponse>): Promise<void> {
        await this.handleAPICall("/login_with_code", {email, code}, result.data, result.isFetching, result.error);
        if (result.error.value === null && result.data.value?.success === true) {
            markAuthenticatedSession();
        }
    }
    async getDueCards(result: ApiRes<GetDueCardsResponse>): Promise<void> {
        return this.handleAPICall("/get_due_cards", {}, result.data, result.isFetching, result.error);
    }
    async rateCard(cardId: number, rating: number, reviewDuration: number, result: ApiRes<RateCardResponse>, clientRequestId: string): Promise<void> {
        return this.handleAPICall("/rate_card", {cardId, rating, reviewDuration, clientRequestId}, result.data, result.isFetching, result.error, undefined, "post", undefined, 15000);
    }
    async deleteReview(language: string, result: ApiRes<DeleteReviewResponse>): Promise<void> {
        return this.handleAPICall("/delete_review", {language}, result.data, result.isFetching, result.error);
    }
    async getFlashcardStats(result: ApiRes<GetFlashcardStatsResponse>): Promise<void> {
        return this.handleAPICall("/get_flashcard_stats", {}, result.data, result.isFetching, result.error);
    }
    async setCardsEnabled(cardIds: number[] = [], wordIds: number[] = [], enabled: boolean, result: ApiRes<SetCardsEnabledResponse>): Promise<void> {
        return this.handleAPICall("/set_cards_enabled", {cardIds, wordIds, enabled}, result.data, result.isFetching, result.error);
    }
    async setAllCardsEnabled(enabled: boolean, result: ApiRes<SetAllCardsEnabledResponse>): Promise<void> {
        return this.handleAPICall("/set_all_cards_enabled", { enabled }, result.data, result.isFetching, result.error);
    }
    async updateWord(wordId: number, wordData: Partial<Word>, result: ApiRes<UpdateWordResponse>): Promise<void> {
        return this.handleAPICall("/update_word", { wordId, ...wordData }, result.data, result.isFetching, result.error);
    }
    async manageWordAudio(
        wordId: number,
        audioSlot: ManageWordAudioSlot,
        action: ManageWordAudioAction,
        wordText: string | null,
        wordWithTransliteration: string | null,
        sentenceText: string | null,
        result: ApiRes<ManageWordAudioResponse>,
    ): Promise<void> {
        return this.handleAPICall(
            "/manage_word_audio",
            { wordId, audioSlot, action, wordText, wordWithTransliteration, sentenceText },
            result.data,
            result.isFetching,
            result.error,
        );
    }
    async addWords(words: AddWordEntry[], result: ApiRes<AddWordsResponse>): Promise<void> {
        return this.handleAPICall("/add_words", { words }, result.data, result.isFetching, result.error);
    }

    async getAppStoreRating(result: ApiRes<GetStoreRatingResponse>): Promise<void> {
        return this.handleAPICall("/get_app_store_rating", {}, result.data, result.isFetching, result.error, undefined, 'get');
    }

    async getPlayStoreRating(result: ApiRes<GetStoreRatingResponse>): Promise<void> {
        return this.handleAPICall("/get_play_store_rating", {}, result.data, result.isFetching, result.error, undefined, 'get');
    }

    async getSessions(language: string | undefined, result: ApiRes<GetSessionsResponse>): Promise<void> {
        return this.handleAPICall("/get_sessions", { language }, result.data, result.isFetching, result.error);
    }

    async getCurriculum(result: ApiRes<GetCurriculumResponse>): Promise<void> {
        await this.handleAPICall('/get_curriculum', {}, result.data, result.isFetching, result.error);
    }

    async getCurriculumForLanguage(language: string, result: ApiRes<GetCurriculumResponse>): Promise<void> {
        await this.handleAPICall('/get_curriculum', { language }, result.data, result.isFetching, result.error);
    }

    async startOnboardingCurriculumGeneration(
        payload: StartOnboardingCurriculumGenerationPayload,
        result: ApiRes<StartOnboardingCurriculumGenerationResponse>,
    ): Promise<void> {
        await this.handleAPICall('/start_onboarding_curriculum_generation', payload, result.data, result.isFetching, result.error);
    }

    async regenerateCurriculum(customPrompt: string | null, result: ApiRes<RegenerateCurriculumResponse>): Promise<void> {
        await this.handleAPICall('/regenerate_curriculum', { customPrompt }, result.data, result.isFetching, result.error);
    }

    async getCurriculumGenerationStatus(language: string, sinceSeconds: number | null, result: ApiRes<GetCurriculumGenerationStatusResponse>): Promise<void> {
        await this.handleAPICall('/get_curriculum_generation_status', { language, sinceSeconds }, result.data, result.isFetching, result.error);
    }

    async regenerateCurrentLesson(kickstartType: string | null, result: ApiRes<RegenerateCurrentLessonResponse>): Promise<void> {
        await this.handleAPICall('/regenerate_current_lesson', { kickstartType }, result.data, result.isFetching, result.error);
    }

    async translateWords(sentence: string, result: ApiRes<TranslateWordsResponse>): Promise<void> {
        return this.handleAPICall("/translate_words", { sentence }, result.data, result.isFetching, result.error);
    }

    async getPhraseDefinition(phrase: string, sentence: string, language: string, result: ApiRes<GetPhraseDefinitionResponse>): Promise<void> {
        return this.handleAPICall("/get_phrase_definition", { phrase, sentence, language }, result.data, result.isFetching, result.error);
    }


    async downloadFlashcardsCSV(result: ApiRes<Blob>, filename?: string): Promise<void> {
        await this.handleAPICall("/export_flashcards_csv", {}, result.data, result.isFetching, result.error, 'blob');
        
        // Handle download after successful API call
        if (result.data.value && !result.error.value) {
            const blob = new Blob([result.data.value], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || `flashcards_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    }

    async getTrialTime(timezone: string, redirectClickId: string | null, installationId: string, result: ApiRes<GetTrialTimeResponse>): Promise<void> {
        return this.handleAPICall("/get_trial_time_for_timezone", { timezone, redirectClickId, installationId }, result.data, result.isFetching, result.error);
    }

    async rateSession(sessionId: string, rating: number, feedback: string | null, result: ApiRes<{}>): Promise<void> {
        const data: { sessionId: string; rating: number; feedback?: string } = { sessionId, rating };
        if (feedback !== null) {
            data.feedback = feedback;
        }
        return this.handleAPICall(
            "/rate_session",
            data,
            result.data,
            result.isFetching,
            result.error,
        );
    }

    async getStreak(result: ApiRes<GetStreakResponse>): Promise<void> {
        return this.handleAPICall("/get_streak", {}, result.data, result.isFetching, result.error);
    }

    async claimStreakScreen(result: ApiRes<ClaimStreakScreenResponse>): Promise<void> {
        return this.handleAPICall("/claim_streak_screen", {}, result.data, result.isFetching, result.error);
    }

    async recordInviteClick(code: string, result: ApiRes<RecordInviteClickResponse>): Promise<void> {
        return this.handleAPICall(
            `/i/${encodeURIComponent(code)}?format=json`,
            {},
            result.data,
            result.isFetching,
            result.error,
            undefined,
            'get',
        );
    }

    async getReferralOffer(result: ApiRes<GetReferralOfferResponse>, markPromptShown: boolean): Promise<void> {
        return this.handleAPICall(
            "/get_referral_offer",
            { markPromptShown },
            result.data,
            result.isFetching,
            result.error,
        );
    }

    async recordReferralShare(result: ApiRes<Record<string, never>>): Promise<void> {
        return this.handleAPICall("/record_referral_share", {}, result.data, result.isFetching, result.error);
    }
}
export {
    PracticeAPI,
}
